/** GL-SPEC-APP-004 Phase A — Document Coverage Engine UI.
 *
 * Bucket-organized rule-driven document list.
 *
 * Computed states (from rules engine, read-only):
 *   - required    : facility must have this document
 *   - conditional : would be required IF a hazard flag is set; shown for awareness
 *   - exempt      : not applicable, CFR exemption cited
 *
 * User states (consultant override, set in this UI):
 *   - present : reviewed and confirmed compliant
 *   - missing : reviewed and confirmed missing/non-compliant
 *   - na      : not applicable for this engagement
 *
 * Tomorrow's MVP: the bucket list with status states and rules driven by Setup.
 * Element-level deep-dive lives on the legacy DocReviewTab for the 9 docs that
 * already have element checklists.
 */
import { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Spinner, KpiTile, SectionHeader, Annotation } from '@/components/Common';
import VoiceDictateButton from '@/components/VoiceDictateButton';
import {
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  WarningCircleIcon,
  ShieldCheckIcon,
  CaretDownIcon,
  CaretRightIcon,
  ArrowSquareOutIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const USER_STATES = [
  { key: 'present', label: 'Present', icon: CheckCircleIcon, color: 'var(--gl-p3)' },
  { key: 'present_with_gaps', label: 'Present - Gaps', icon: WarningCircleIcon, color: 'var(--gl-gold)' },
  { key: 'needs_revision', label: 'Needs Revision', icon: WarningCircleIcon, color: 'var(--gl-p2)' },
  { key: 'missing', label: 'Missing', icon: XCircleIcon, color: 'var(--gl-p1)' },
  { key: 'na', label: 'N/A', icon: MinusCircleIcon, color: 'var(--gl-muted)' },
];

const COMPUTED_BADGE = {
  required: { label: 'Required', color: 'var(--gl-navy)', bg: '#eff4fb' },
  conditional: { label: 'Conditional', color: 'var(--gl-p2)', bg: '#fef4ea' },
  exempt: { label: 'Exempt', color: 'var(--gl-muted)', bg: '#f3f4f6' },
};

const USER_BADGE = {
  present: { label: 'Present', color: '#fff', bg: 'var(--gl-p3)' },
  present_with_gaps: { label: 'Present - Gaps Noted', color: '#1c2b2b', bg: 'var(--gl-gold)' },
  needs_revision: { label: 'Needs Revision', color: '#fff', bg: 'var(--gl-p2)' },
  missing: { label: 'Missing', color: '#fff', bg: 'var(--gl-p1)' },
  na: { label: 'N/A', color: '#fff', bg: 'var(--gl-muted)' },
};


export default function DocCoverageTab({ eng, focusDocKey, onFocusConsumed, onJumpToFinding }) {
  const [coverage, setCoverage] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [highlightKey, setHighlightKey] = useState(null);
  // GL-SPEC-APP-037 — Field filter: hide compliant docs to surface only
  // Missing / Present-with-Gaps / Needs-Revision rows.
  const [showOnlyGaps, setShowOnlyGaps] = useState(false);

  // GL-SPEC-APP-019 — deep-link from Findings tab: expand the bucket
  // containing focusDocKey, scroll into view, and pulse a gold highlight.
  useEffect(() => {
    if (!focusDocKey || !coverage) return;
    const bucket = coverage.buckets.find((b) =>
      (b.docs || []).some((d) => d.key === focusDocKey)
    );
    if (!bucket) return;
    setExpanded((prev) => ({ ...prev, [bucket.key]: true }));
    setHighlightKey(focusDocKey);
    // Wait one frame for the bucket to expand, then scroll
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-doc-row="${focusDocKey}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFocusConsumed && onFocusConsumed();
    }, 80);
    // Clear the highlight after 2.5s
    const clear = setTimeout(() => setHighlightKey(null), 2500);
    return () => {
      clearTimeout(t);
      clearTimeout(clear);
    };
  }, [focusDocKey, coverage, onFocusConsumed]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/engagements/${eng.id}/doc_coverage`);
      setCoverage(data);
      // Default-expand the first bucket only
      setExpanded((prev) => {
        if (Object.keys(prev).length) return prev;
        const first = data.buckets[0]?.key;
        return first ? { [first]: true } : {};
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [eng.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function setUserState(docKey, nextState, currentState) {
    setBusy((b) => ({ ...b, [docKey]: true }));
    try {
      // Toggle off if clicking the same chip again
      const payload = { user_state: currentState === nextState ? null : nextState };
      const { data } = await api.patch(`/engagements/${eng.id}/doc_coverage/${docKey}`, payload);
      setCoverage(data.coverage);
      // GL-SPEC-APP-009 - surface what the auto-finding sync did
      const af = data.auto_finding;
      if (af) {
        if (af.action === 'created') {
          toast.success(`Finding #${af.number} auto-created (${af.priority}). Tap Findings tab to view.`);
        } else if (af.action === 'updated') {
          toast.success(`Linked finding updated to ${af.priority}.`);
        } else if (af.action === 'removed') {
          toast.success('Linked auto-finding removed.');
        } else if (af.action === 'skipped_manual_owned') {
          toast.message('Linked finding preserved (you have manual edits on it).');
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [docKey]: false }));
    }
  }

  async function setNotes(docKey, notes) {
    try {
      const { data } = await api.patch(`/engagements/${eng.id}/doc_coverage/${docKey}`, { notes });
      setCoverage(data.coverage);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function setCorrective(docKey, corrective_action) {
    try {
      const { data } = await api.patch(
        `/engagements/${eng.id}/doc_coverage/${docKey}`,
        { corrective_action }
      );
      setCoverage(data.coverage);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  // GL-SPEC-APP-027 — Toggle whether this doc generates a standalone finding card.
  async function setIncludeAsFinding(docKey, include_as_finding) {
    setBusy((b) => ({ ...b, [docKey]: true }));
    try {
      const { data } = await api.patch(
        `/engagements/${eng.id}/doc_coverage/${docKey}`,
        { include_as_finding }
      );
      setCoverage(data.coverage);
      const af = data.auto_finding;
      if (!include_as_finding) {
        if (af?.action === 'removed') {
          toast.success('Auto-finding suppressed. Doc still appears in Coverage Summary.');
        } else {
          toast.success('Suppressed: no standalone finding will print for this doc.');
        }
      } else {
        if (af?.action === 'created') {
          toast.success(`Auto-finding restored as Finding #${af.number}.`);
        } else if (af?.action === 'updated') {
          toast.success('Auto-finding restored.');
        } else {
          toast.success('Auto-finding restored.');
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [docKey]: false }));
    }
  }

  if (!coverage) return <Spinner label="Loading document coverage…" />;

  const t = coverage.totals;
  const GAP_STATES = ['missing', 'present_with_gaps', 'needs_revision'];
  const gapTotal = (t.missing || 0) + (t.present_with_gaps || 0) + (t.needs_revision || 0);

  // When the filter is on, also auto-expand every bucket that has any gap rows
  // so the consultant doesn't have to tap each one open.
  function filteredBuckets() {
    if (!showOnlyGaps) return coverage.buckets;
    return coverage.buckets
      .map((b) => ({
        ...b,
        docs: (b.docs || []).filter((d) => GAP_STATES.includes(d.user_state)),
        not_applicable: [],
      }))
      .filter((b) => (b.docs || []).length > 0);
  }
  const visibleBuckets = filteredBuckets();
  const buckets = visibleBuckets;

  return (
    <div className="max-w-5xl">
      {/* GL-DESIGN-2026Q2 — Header KPI stat hero (Plausible-style).
          Required is the anchor; missing/gaps/needs-revision tinted to
          signal risk; present/exempt/na de-emphasized so the eye lands
          on what needs work. */}
      <SectionHeader
        eyebrow="Document Coverage"
        title="Required docs by facility profile"
        right={
          <Link
            to="setup"
            className="gl-mono text-xs font-bold underline"
            style={{ color: 'var(--gl-navy)' }}
          >
            adjust setup →
          </Link>
        }
      />
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-2" data-testid="doc-coverage-kpi-grid">
        <KpiTile label="Required" value={t.required || 0} valueTone="navy" testid="cov-stat-required" />
        <KpiTile label="Missing" value={t.missing || 0} valueTone="p1" stripe={(t.missing || 0) > 0 ? 'p1' : undefined} testid="cov-stat-missing" />
        <KpiTile label="Gaps" value={t.present_with_gaps || 0} valueTone="gold" stripe={(t.present_with_gaps || 0) > 0 ? 'gold' : undefined} testid="cov-stat-present-with-gaps" />
        <KpiTile label="Needs revision" value={t.needs_revision || 0} valueTone="p2" stripe={(t.needs_revision || 0) > 0 ? 'p2' : undefined} testid="cov-stat-needs-revision" />
        <KpiTile label="Present" value={t.present || 0} valueTone="p3" testid="cov-stat-present" />
        <KpiTile label="N/A · exempt" value={(t.na || 0) + (t.exempt || 0)} testid="cov-stat-na" />
      </div>
      <Annotation>
        Computed from Setup — industry, headcount, fleet, and hazard flags. Mark each as Present, Missing, or N/A as you walk the floor.
      </Annotation>

      {/* GL-SPEC-APP-037 — Show-only-gaps filter pill row */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="doc-filter-row">
        <button
          type="button"
          onClick={() => setShowOnlyGaps((v) => !v)}
          className="px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-widest border-2 transition-all inline-flex items-center gap-2"
          style={{
            background: showOnlyGaps ? 'var(--gl-dark)' : '#fff',
            color: showOnlyGaps ? 'var(--gl-gold)' : 'var(--gl-dark)',
            borderColor: showOnlyGaps ? 'var(--gl-gold)' : '#e5e3da',
            fontFamily: 'Inter',
          }}
          data-testid="doc-filter-show-only-gaps"
          aria-pressed={showOnlyGaps}
          title="Hide compliant docs and show only Missing / Present-with-Gaps / Needs-Revision rows"
        >
          {showOnlyGaps ? '✓ Showing gaps only' : 'Show only gaps'}
          <span
            className="ml-1 px-1.5 py-0.5 rounded-sm"
            style={{
              background: showOnlyGaps ? 'var(--gl-gold)' : 'var(--gl-dark)',
              color: showOnlyGaps ? 'var(--gl-dark)' : '#fff',
            }}
          >
            {gapTotal}
          </span>
        </button>
        {showOnlyGaps && buckets.length === 0 && (
          <span className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-p3)', fontFamily: 'Inter' }}>
            ✓ No open gaps — every reviewed doc is compliant
          </span>
        )}
      </div>

      {buckets.map((b) => (
        <BucketCard
          key={b.key}
          bucket={b}
          expanded={showOnlyGaps ? true : !!expanded[b.key]}
          onToggle={() => setExpanded((p) => ({ ...p, [b.key]: !p[b.key] }))}
          onSetState={setUserState}
          onSetNotes={setNotes}
          onSetCorrective={setCorrective}
          onSetIncludeAsFinding={setIncludeAsFinding}
          onJumpToFinding={onJumpToFinding}
          highlightKey={highlightKey}
          busy={busy}
        />
      ))}
    </div>
  );
}

function StatPill({ label, value, bg, color, testid }) {
  return (
    <div
      className="px-3 py-1.5 rounded-sm flex items-baseline gap-2"
      style={{ background: bg, color }}
      data-testid={testid}
    >
      <span className="font-display font-extrabold text-lg leading-none">{value}</span>
      <span
        className="text-xs uppercase tracking-wider font-bold"
        style={{ fontFamily: 'Inter', letterSpacing: '0.06em' }}
      >
        {label}
      </span>
    </div>
  );
}

function BucketCard({ bucket, expanded, onToggle, onSetState, onSetNotes, onSetCorrective, onSetIncludeAsFinding, busy, highlightKey, onJumpToFinding }) {
  const required = bucket.counts.required || 0;
  const missing = bucket.counts.missing || 0;
  const present = bucket.counts.present || 0;
  const notApplicable = (bucket.not_applicable || []).length;
  const hasApplicable = bucket.docs.length > 0;
  const [showNotApplicable, setShowNotApplicable] = useState(false);

  // If the entire bucket is "not applicable to this facility" (e.g., FMCSA when no fleet),
  // render a slim collapsed peek instead of the full card.
  if (!hasApplicable && notApplicable > 0) {
    return (
      <div className="gl-card mb-3" data-testid={`bucket-${bucket.key}`} style={{ background: '#fafaf7' }}>
        <button
          type="button"
          className="w-full flex items-center gap-3 text-left"
          onClick={() => setShowNotApplicable((v) => !v)}
          data-testid={`bucket-toggle-${bucket.key}`}
        >
          {showNotApplicable ? (
            <CaretDownIcon size={18} weight="bold" color="var(--gl-muted)" />
          ) : (
            <CaretRightIcon size={18} weight="bold" color="var(--gl-muted)" />
          )}
          <div className="flex-1">
            <div
              className="font-display font-extrabold uppercase text-base"
              style={{ color: 'var(--gl-muted)' }}
            >
              {bucket.label}
            </div>
            <div
              className="text-xs uppercase tracking-wider mt-0.5"
              style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.06em' }}
            >
              Not applicable to this facility · {notApplicable} docs hidden · adjust hazard flags in Setup if this is wrong
            </div>
          </div>
        </button>
        {showNotApplicable && (
          <div className="mt-3 space-y-2">
            {bucket.not_applicable.map((d) => (
              <DocRow key={d.key} doc={d} onSetState={onSetState} onSetNotes={onSetNotes} busy={!!busy[d.key]} dimmed />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="gl-card mb-3" data-testid={`bucket-${bucket.key}`}>
      <button
        type="button"
        className="w-full flex items-center gap-3 text-left"
        onClick={onToggle}
        data-testid={`bucket-toggle-${bucket.key}`}
      >
        {expanded ? (
          <CaretDownIcon size={20} weight="bold" color="var(--gl-navy)" />
        ) : (
          <CaretRightIcon size={20} weight="bold" color="var(--gl-navy)" />
        )}
        <div className="flex-1">
          <div
            className="font-display font-extrabold uppercase text-lg"
            style={{ color: 'var(--gl-dark)' }}
          >
            {bucket.label}
          </div>
          <div
            className="text-xs uppercase tracking-wider mt-0.5"
            style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.06em' }}
          >
            {bucket.docs.length} documents
            {required ? ` · ${required} required` : ''}
            {present ? ` · ${present} present` : ''}
            {missing ? ` · ${missing} missing` : ''}
          </div>
        </div>
        <div className="flex gap-1">
          {missing > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider"
              style={{ background: 'var(--gl-p1)', color: '#fff', fontFamily: 'Inter', letterSpacing: '0.06em' }}
            >
              {missing} Missing
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          {bucket.docs.map((d) => (
            <DocRow
              key={d.key}
              doc={d}
              onSetState={onSetState}
              onSetNotes={onSetNotes}
              onSetCorrective={onSetCorrective}
              onSetIncludeAsFinding={onSetIncludeAsFinding}
              onJumpToFinding={onJumpToFinding}
              highlight={highlightKey === d.key}
              busy={!!busy[d.key]}
            />
          ))}

          {notApplicable > 0 && (
            <div
              className="mt-2 p-2 border-2 border-dashed rounded-sm"
              style={{ borderColor: '#d1d5db', background: '#fafaf7' }}
            >
              <button
                type="button"
                className="w-full flex items-center gap-2 text-left"
                onClick={() => setShowNotApplicable((v) => !v)}
                data-testid={`bucket-na-toggle-${bucket.key}`}
              >
                {showNotApplicable ? (
                  <CaretDownIcon size={14} weight="bold" color="var(--gl-muted)" />
                ) : (
                  <CaretRightIcon size={14} weight="bold" color="var(--gl-muted)" />
                )}
                <span
                  className="text-xs uppercase tracking-wider font-bold"
                  style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.06em' }}
                >
                  {notApplicable} more not applicable to this facility — tap to reveal (adjust hazard flags in Setup if needed)
                </span>
              </button>
              {showNotApplicable && (
                <div className="mt-2 space-y-2">
                  {bucket.not_applicable.map((d) => (
                    <DocRow key={d.key} doc={d} onSetState={onSetState} onSetNotes={onSetNotes} busy={!!busy[d.key]} dimmed />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DocRow({ doc, onSetState, onSetNotes, onSetCorrective, onSetIncludeAsFinding, onJumpToFinding, busy, dimmed = false, highlight = false }) {
  const [showNotes, setShowNotes] = useState(!!doc.notes || !!doc.corrective_action);
  const [notesDraft, setNotesDraft] = useState(doc.notes || '');
  const [caDraft, setCaDraft] = useState(doc.corrective_action || '');
  // GL-SPEC-APP-027 — local mirror so the switch reacts instantly while the
  // PATCH round-trips. Default true (legacy rows have no value).
  const includeAsFinding = doc.include_as_finding !== false;

  const computed = COMPUTED_BADGE[doc.computed_state];
  const user = doc.user_state ? USER_BADGE[doc.user_state] : null;

  // Visual emphasis for high-priority states
  const borderColor =
    doc.user_state === 'missing'
      ? 'var(--gl-p1)'
      : doc.user_state === 'needs_revision'
      ? 'var(--gl-p2)'
      : doc.user_state === 'present_with_gaps'
      ? 'var(--gl-gold)'
      : doc.user_state === 'present'
      ? 'var(--gl-p3)'
      : doc.computed_state === 'conditional'
      ? 'var(--gl-p2)'
      : '#e5e7eb';

  return (
    <div
      className={`p-3 border-l-4 border-2 rounded-sm bg-white transition-shadow duration-300 ${highlight ? 'gl-doc-row-highlight' : ''}`}
      style={{
        borderLeftColor: borderColor,
        borderColor: highlight ? 'var(--gl-gold)' : '#e5e7eb',
        opacity: dimmed ? 0.7 : 1,
        boxShadow: highlight ? '0 0 0 3px rgba(201, 168, 76, 0.4)' : 'none',
      }}
      data-testid={`doc-${doc.key}`}
      data-doc-row={doc.key}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-sm" style={{ color: 'var(--gl-dark)' }}>
              {doc.label}
            </div>
            {doc.has_element_checklist && (
              <span
                className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm"
                style={{
                  background: '#fdf6e3',
                  color: 'var(--gl-dark)',
                  border: '1px solid var(--gl-gold)',
                  fontFamily: 'Inter',
                  letterSpacing: '0.06em',
                }}
                title="Element-level checklist available — open the Doc Review tab"
              >
                Element Checklist
              </span>
            )}
            {/* GL-SPEC-APP-037 — Has-notes indicator so consultants can scan
                long bucket lists and spot the rows that already carry content. */}
            {(doc.notes || doc.corrective_action) && (
              <span
                className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
                style={{
                  background: 'var(--gl-navy)',
                  color: '#fff',
                  fontFamily: 'Inter',
                  letterSpacing: '0.06em',
                }}
                title={`Has ${doc.notes ? 'notes' : ''}${doc.notes && doc.corrective_action ? ' + ' : ''}${doc.corrective_action ? 'corrective action' : ''}`}
                data-testid={`doc-has-notes-${doc.key}`}
              >
                ✎ {doc.notes && doc.corrective_action ? 'NOTES + CA' : doc.corrective_action ? 'CA' : 'NOTES'}
              </span>
            )}
          </div>
          <div
            className="gl-mono text-xs mt-0.5"
            style={{ color: '#6b7280', letterSpacing: '0.01em' }}
          >
            {doc.cfr}
          </div>
          {doc.note && (
            <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
              {doc.note}
            </div>
          )}
          {doc.computed_state === 'exempt' && doc.exemption_label && (
            <div
              className="text-xs mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm"
              style={{ background: '#f3f4f6', color: 'var(--gl-muted)' }}
            >
              <WarningCircleIcon size={14} weight="bold" />
              {doc.exemption_label} ({doc.exemption_cfr})
            </div>
          )}
          {/* GL-SPEC-APP-019 — reverse-link to the linked finding */}
          {doc.linked_finding && onJumpToFinding && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onJumpToFinding(doc.linked_finding.id);
              }}
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm inline-flex items-center gap-1 mt-1.5 hover:opacity-80 transition-opacity cursor-pointer"
              style={{
                background: doc.linked_finding.auto_generated ? 'var(--gl-gold)' : '#f3f4f6',
                color: doc.linked_finding.auto_generated ? '#1c2b2b' : 'var(--gl-dark)',
                fontFamily: 'Inter',
                letterSpacing: '0.06em',
                border: 'none',
              }}
              title={
                doc.linked_finding.auto_generated
                  ? `Auto-generated Finding #${doc.linked_finding.number}. Click to jump to it.`
                  : `Linked Finding #${doc.linked_finding.number} (manually owned). Click to jump to it.`
              }
              data-testid={`doc-${doc.key}-linked-finding`}
            >
              {doc.linked_finding.auto_generated ? 'AUTO -' : 'LINKED'} FINDING #{doc.linked_finding.number}
              <span aria-hidden="true" style={{ fontSize: '11px', lineHeight: 1 }}>→</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Computed badge (always shown) */}
          <span
            className="text-xs px-2 py-1 rounded-sm font-bold uppercase tracking-wider"
            style={{
              background: computed?.bg,
              color: computed?.color,
              fontFamily: 'Inter',
              letterSpacing: '0.06em',
            }}
            title="Computed from Setup inputs"
          >
            {computed?.label}
          </span>

          {/* User state chips */}
          <div className="flex gap-1">
            {USER_STATES.map((s) => {
              const active = doc.user_state === s.key;
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  className="px-2 py-1 rounded-sm border-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wider transition-all"
                  style={{
                    background: active ? s.color : '#fff',
                    color: active ? '#fff' : s.color,
                    borderColor: s.color,
                    opacity: busy ? 0.5 : 1,
                    fontFamily: 'Inter',
                    letterSpacing: '0.06em',
                  }}
                  onClick={() => onSetState(doc.key, s.key, doc.user_state)}
                  disabled={busy}
                  data-testid={`doc-${doc.key}-${s.key}`}
                  title={`Mark this document as ${s.label}`}
                >
                  <Icon size={14} weight="bold" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          className="text-xs underline"
          style={{ color: 'var(--gl-navy)' }}
          onClick={() => setShowNotes((v) => !v)}
          data-testid={`doc-${doc.key}-notes-toggle`}
        >
          {showNotes ? 'Hide notes' : doc.notes ? 'View notes' : 'Add notes'}
        </button>
        {user && (
          <span
            className="text-xs px-2 py-0.5 rounded-sm font-bold"
            style={{ background: user.bg, color: user.color, fontFamily: 'Inter', letterSpacing: '0.06em' }}
          >
            CONSULTANT: {user.label}
          </span>
        )}
        {/* GL-SPEC-APP-027 — visible badge when this doc is suppressed from PDF */}
        {!includeAsFinding && (doc.user_state === 'missing' || doc.user_state === 'needs_revision' || doc.user_state === 'present_with_gaps') && (
          <span
            className="text-[10px] px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider"
            style={{
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid var(--gl-gold)',
              fontFamily: 'Inter',
              letterSpacing: '0.06em',
            }}
            data-testid={`doc-${doc.key}-suppressed-badge`}
            title="This doc will not print as a standalone finding card. It still appears in the Document Coverage Summary."
          >
            Finding Suppressed
          </span>
        )}
      </div>

      {showNotes && (
        <div className="mt-2 space-y-2">
          <div>
            <div className="gl-label flex items-center justify-between" style={{ marginBottom: 4 }}>
              <span>Gap Notes</span>
              <VoiceDictateButton
                testid={`doc-${doc.key}-notes-dictate`}
                onTranscript={(text) => {
                  const combined = notesDraft ? `${notesDraft} ${text}`.trim() : text;
                  setNotesDraft(combined);
                  onSetNotes(doc.key, combined);
                }}
              />
            </div>
            <textarea
              className="gl-input gl-textarea"
              rows={2}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => {
                if (notesDraft !== (doc.notes || '')) onSetNotes(doc.key, notesDraft);
              }}
              placeholder="What's missing or out of compliance with this document?"
              data-testid={`doc-${doc.key}-notes`}
            />
          </div>
          {/* GL-SPEC-APP-018 — consultant-authored Corrective Action that flows
              verbatim into the auto-generated finding. Hidden when state is
              benign (present/na) because no auto-finding gets created. */}
          {(doc.user_state === 'missing' || doc.user_state === 'needs_revision' || doc.user_state === 'present_with_gaps') && (
            <div>
              <div className="gl-label" style={{ marginBottom: 4 }}>
                Corrective Action
                <span className="ml-2 text-[10px] font-normal" style={{ color: 'var(--gl-muted)' }}>
                  Used verbatim on the report. Leave blank for templated language.
                </span>
              </div>
              <textarea
                className="gl-input gl-textarea"
                rows={2}
                value={caDraft}
                onChange={(e) => setCaDraft(e.target.value)}
                onBlur={() => {
                  if (caDraft !== (doc.corrective_action || '')) onSetCorrective(doc.key, caDraft);
                }}
                placeholder="What should the client do to close this gap? (Your text appears on the deliverable.)"
                data-testid={`doc-${doc.key}-corrective`}
              />
            </div>
          )}

          {/* GL-SPEC-APP-027 — De-duplication switch. When the same gap is
              already covered by a narrative finding, flip this OFF so the
              auto-generated finding card does not also print. The doc still
              appears in the Document Coverage Summary table. */}
          {(doc.user_state === 'missing' || doc.user_state === 'needs_revision' || doc.user_state === 'present_with_gaps') && onSetIncludeAsFinding && (
            <div
              className="rounded-sm border p-2.5 flex items-start gap-3"
              style={{
                background: includeAsFinding ? '#f9fafb' : '#fef9ec',
                borderColor: includeAsFinding ? '#e5e7eb' : 'var(--gl-gold)',
              }}
            >
              <button
                type="button"
                role="switch"
                aria-checked={includeAsFinding}
                onClick={() => onSetIncludeAsFinding(doc.key, !includeAsFinding)}
                disabled={busy}
                data-testid={`doc-${doc.key}-include-as-finding`}
                className="relative inline-flex items-center flex-shrink-0 mt-0.5"
                style={{
                  width: 36,
                  height: 20,
                  borderRadius: 999,
                  background: includeAsFinding ? 'var(--gl-p3)' : 'var(--gl-muted)',
                  transition: 'background 150ms ease',
                  border: 'none',
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
                title={
                  includeAsFinding
                    ? 'This doc generates its own finding card on the PDF. Toggle off to suppress (use when a narrative finding already covers it).'
                    : 'This doc is SUPPRESSED — no standalone finding card will print. Doc Coverage Summary still shows it.'
                }
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: includeAsFinding ? 18 : 2,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 150ms ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }}
                />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}>
                  {includeAsFinding ? 'Include as standalone finding' : 'Already covered by narrative finding (suppressed)'}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: 'var(--gl-muted)' }}>
                  {includeAsFinding
                    ? 'A separate finding card will print on the PDF for this document.'
                    : 'This document still appears in the Document Coverage Summary, but no duplicate finding card will print.'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
